<template>
  <v-container
    fluid
    tag="section"
  >
    <v-card>
      <v-card-text>
        <v-row dense>
          <v-col
            cols="12"
            sm="3"
          >
            <v-text-field
              v-model="search"
              clearable
              outlined
              dense
              hide-details
              label="Номер карты (card_uid)"
              prepend-inner-icon="mdi-magnify"
              @keyup.enter="applySearch"
            />
          </v-col>
          <v-col
            cols="12"
            sm="2"
          >
            <v-btn
              color="primary"
              outlined
              @click="$refs.filter.open()"
            >
              <v-icon class="ml-3">
                mdi-filter-variant
              </v-icon>
              <span class="mx-3">Фильтры</span>
            </v-btn>
          </v-col>
          <v-col
            cols="12"
            sm="4"
            class="d-sm-flex justify-end"
          >
            <v-btn
              color="primary"
              @click="$refs.upsert.open()"
            >
              <v-icon class="ml-3">
                mdi-card-account-details
              </v-icon>
              <span class="mx-3">Выдать пропуск</span>
            </v-btn>
            <v-btn
              icon
              title="Выгрузить CSV"
              @click="csv"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn
              icon
              title="Настройка столбцов"
              @click="openSkudHeaders()"
            >
              <v-icon>mdi-view-column</v-icon>
            </v-btn>
            <v-btn
              icon
              title="Обновить"
              @click="reset(); load()"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-alert
          v-if="items.length >= LIMIT"
          type="warning"
          outlined
          dense
          class="mt-4 mb-0"
        >
          Показаны первые {{ LIMIT }} записей, сузьте фильтры
        </v-alert>
        <!-- ponytail: клиентская сортировка/пагинация только по загруженным N; серверный paging — когда бэкенд добавит page/total -->
        <v-data-table
          :headers="headers.filter(a => a.show || a.active)"
          :items="items"
          :loading="loading"
          :footer-props="datatable_footer"
          no-data-text="Нет данных для отображения"
        >
          <template v-slot:footer.page-text="{pageStart,pageStop,itemsLength}">
            {{ pageStart }}-{{ pageStop }} из {{ itemsLength }}
          </template>
          <template v-slot:item.grant_state="{item}">
            <v-chip
              v-if="item.grant_state_text"
              x-small
              :color="grantStateColor(item.grant_state)"
              text-color="white"
            >
              {{ item.grant_state_text }}
            </v-chip>
            <span v-else>—</span>
          </template>
          <template v-slot:item.cards="{item}">
            <v-chip
              v-for="c in item.cards"
              :key="c.credential_id"
              small
              class="ma-1"
              :color="cardColor(c)"
              text-color="white"
              @click="openCredential(c, item)"
            >
              {{ c.uid }} · {{ c.kind_text }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{item}">
            <skud-actions-menu>
              <v-list-item @click="$refs.upsert.open(item.subject_ref)">
                <v-list-item-content>
                  Заменить/выдать карту
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="hasCards(item)"
                @click="$refs.qr.open(item)"
              >
                <v-list-item-content>
                  QR карты
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="blockable(item)"
                @click="$refs.state.open(item, 2)"
              >
                <v-list-item-content>
                  Заблокировать карту
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="unblockable(item)"
                @click="$refs.state.open(item, 0)"
              >
                <v-list-item-content>
                  Разблокировать карту
                </v-list-item-content>
              </v-list-item>
              <v-list-item @click="revoke(item)">
                <v-list-item-content>
                  Отозвать
                </v-list-item-content>
              </v-list-item>
            </skud-actions-menu>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <skud-filter-dialog
      ref="filter"
      :fields="filterFields"
      storage-key="skud.card"
      @apply="reset(); load()"
    />

    <v-dialog
      v-model="dialog_headers.show"
      persistent
      max-width="700px"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Настройка столбцов</span>
        </v-card-title>
        <v-card-text>
          <v-data-table
            dense
            :items-per-page="10"
            :items="headers.filter(a => !a.show)"
            :headers="[{value: 'text', text: 'Название столбца'}, {value: 'active_temp', text: 'Отображение'}]"
            :footer-props="datatable_footer"
            no-data-text="Нет данных для отображения"
          >
            <template v-slot:item.active_temp="{item}">
              <v-checkbox v-model="item.active_temp" />
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="saveSkudHeaders('skud_card')"
          >
            <span class="mx-4">Сохранить</span>
          </v-btn>
          <v-btn
            color="secondary darken-1"
            outlined
            rounded
            small
            class="ml-2 mb-2"
            @click="dialog_headers.show = false"
          >
            <span class="mx-4">Закрыть</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <skud-card-credential-dialog ref="credential" />
    <skud-card-qr-dialog ref="qr" />
    <skud-card-state-dialog
      ref="state"
      @done="reset(); load()"
    />
    <skud-pass-upsert-dialog
      ref="upsert"
      @saved="reset(); load()"
    />
  </v-container>
</template>

<script>
import SkudFilterDialog from '@/views/skud/components/SkudFilterDialog.vue'
import SkudCardCredentialDialog from '@/views/skud/components/SkudCardCredentialDialog.vue'
import SkudCardQrDialog from '@/views/skud/components/SkudCardQrDialog.vue'
import SkudCardStateDialog from '@/views/skud/components/SkudCardStateDialog.vue'
import SkudPassUpsertDialog from '@/views/skud/components/SkudPassUpsertDialog.vue'
import SkudActionsMenu from '@/views/skud/components/SkudActionsMenu.vue'
import { headersMixin } from '@/views/skud/shared/modules/headers-mixin'
import { SkudCardService } from '@/views/skud/shared/services/skud-card-service'
import { downloadCsvFile } from '@/views/loyalty/shared/modules/utilities'
import { CredentialIssuer, CredentialState, enumOptions, grantStateColor } from '@/views/skud/shared/models/enums'

export default {
  name: 'SkudCardList',
  components: {
    SkudFilterDialog,
    SkudCardCredentialDialog,
    SkudCardQrDialog,
    SkudCardStateDialog,
    SkudPassUpsertDialog,
    SkudActionsMenu
  },
  mixins: [headersMixin],
  data () {
    return {
      svc: null,
      items: [],
      loading: false,
      search: '',
      LIMIT: 500,
      datatable_footer: {
        itemsPerPageText: 'Показывать по',
        pageText: 'из',
        itemsPerPageOptions: [5, 10, 15, 30, 50]
      },
      headers: [
        { text: 'Действия', value: 'actions', show: true, sortable: false },
        { text: 'Субъект', value: 'subject_ref', show: true },
        { text: 'Место', value: 'place_name', active: true, active_temp: true },
        { text: 'Роль', value: 'role_code', active: true, active_temp: true },
        { text: 'Группа', value: 'group_ref', active: true, active_temp: true },
        { text: 'Пропуск', value: 'grant_state', active: true, active_temp: true },
        { text: 'Действует с', value: 'valid_from', active: true, active_temp: true },
        { text: 'Действует по', value: 'valid_to', active: true, active_temp: true },
        { text: 'Карты', value: 'cards', sortable: false, active: true, active_temp: true }
      ],
      filterFields: {
        card: {
          index: 2,
          name: 'Карта',
          data: [
            { name: 'Эмитент', field: 'issuer', type: 'select', select: enumOptions(CredentialIssuer) },
            { name: 'Состояние', field: 'state', type: 'select', select: enumOptions(CredentialState) },
            { name: 'Номер карты (card_uid)', field: 'card_uid', type: 'text' }
          ]
        },
        subject: {
          index: 3,
          name: 'Субъект',
          data: [
            { name: 'Субъект (subject_ref)', field: 'subject_ref', type: 'text' },
            { name: 'Группа (group_ref)', field: 'group_ref', type: 'text' }
          ]
        }
      }
    }
  },
  mounted () {
    this.svc = new SkudCardService()
    // Старые колонки active_uids/replaced объединены в cards — мигрируем сохранённый набор.
    const saved = this.$store.state.user.skud_card_headers || []
    this.setDefaultSkudHeaders(saved.map(h => (h === 'active_uids' || h === 'replaced') ? 'cards' : h))
    this.load()
  },
  methods: {
    grantStateColor,
    reset () {
      this.items = []
    },
    applySearch () {
      this.reset()
      this.load()
    },
    buildParams () {
      const params = Object.assign({}, this.$refs.filter.current())
      delete params.from // вкладка «Период» убрана; чистим остатки из localStorage
      delete params.to
      if (this.search) params.card_uid = this.search
      return params
    },
    async load () {
      this.loading = true
      this.items = await this.svc.getCards(null, this.buildParams())
      this.loading = false
      if (this.svc.error) {
        this.$store.commit('snackbar', this.svc.error)
      }
    },
    // CSV собираем на клиенте из загруженных items: формат не зависит от внешнего Charon API.
    // Колонки — видимые в таблице (show || active), кроме «Действий».
    csv () {
      const esc = v => {
        const s = v === null || v === undefined ? '' : String(v)
        return /[;"\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
      }
      const cell = {
        grant_state: i => i.grant_state_text,
        cards: i => (i.cards || []).map(c => c.uid).join(', ')
      }
      const cols = this.headers.filter(h => h.value !== 'actions' && (h.show || h.active))
      const rows = [cols.map(h => esc(h.text)).join(';')]
      for (const item of this.items) rows.push(cols.map(h => esc((cell[h.value] || (i => i[h.value]))(item))).join(';'))
      downloadCsvFile('cards.csv', '\uFEFF' + rows.join('\r\n'))
      this.$store.commit('snackbar', 'CSV выгружен')
    },
    openCredential (c, item) {
      this.$refs.credential.open(c.credential_id, item.place_id)
    },
    hasCards (item) {
      return (item.active_uids || []).some(c => Number(c.kind) === 0)
    },
    blockable (item) {
      return (item.cards || []).some(c => Number(c.state) === 0)
    },
    unblockable (item) {
      return (item.cards || []).some(c => Number(c.state) === 2)
    },
    async revoke (item) {
      if (!confirm('Отозвать пропуск субъекта ' + item.subject_ref + '?')) return
      const res = await this.svc.revokePass(item.place_id, item.subject_ref)
      if (res.ok) {
        this.$store.commit('snackbar', 'Пропуск отозван, rev ' + res.data.revision)
        this.reset()
        this.load()
      } else {
        this.$store.commit('snackbar', res.error)
      }
    },
    issuerColor (issuer) {
      switch (Number(issuer)) {
        case 0: return 'blue'
        case 1: return 'green'
        case 2: return 'grey'
        case 3: return 'orange'
        default: return 'grey'
      }
    },
    // Активная карта красится по эмитенту, заменённая — оранжевым, заблокированная — красным.
    cardColor (c) {
      switch (Number(c.state)) {
        case 1: return 'orange'
        case 2: return 'red'
        default: return this.issuerColor(c.issuer)
      }
    }
  }
}
</script>
